import {
  AuthFormError,
  AuthFormHeading,
  AUTH_FORM_SPACING,
  AUTH_PRIMARY_BUTTON,
} from './auth-form-presentation';
import { ButtonLoader } from '@/components/loaders';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useId } from 'react';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import type { useSignInController } from './use-sign-in-controller';
export function SignInVerificationView(
  model: ReturnType<typeof useSignInController>
) {
  const otpInputId = useId();
  const {
    authBasePath,
    router,
    searchParams,
    copy,
    isLoading,
    error,
    step,
    reactivationPhase,
    pendingEmail,
    otpCode,
    setOtpCode,
    isOtpCodeComplete,
    otpContainerRef,
    goToSignInStep,
    handleOtpSubmit,
    handleResendCode,
    handleStartReactivation,
    handleReactivationSubmit,
  } = model;
  // Show OTP verification step
  if (step === 'otp') {
    return (
      <>
        <div className="w-full min-w-0">
          <div>
            <AuthFormHeading
              title={<>{copy.checkYourEmail}</>}
              description={
                <>
                  {copy.verificationCodeSent}{' '}
                  <span className="font-medium">{pendingEmail}</span>
                </>
              }
            />
          </div>
          <div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleOtpSubmit();
              }}
              className={AUTH_FORM_SPACING}
            >
              <div className="flex flex-col items-center">
                <Label
                  htmlFor={otpInputId}
                  className="text-center text-sm font-medium"
                >
                  {copy.verificationCode}
                </Label>
                <div ref={otpContainerRef} className="w-full">
                  <InputOTP
                    id={otpInputId}
                    maxLength={6}
                    autoFocus
                    containerClassName="justify-center"
                    value={otpCode}
                    onChange={setOtpCode}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <AuthFormError>{error}</AuthFormError>

              <Button
                type="submit"
                className={AUTH_PRIMARY_BUTTON}
                disabled={isLoading || !isOtpCodeComplete}
              >
                <ButtonLoader
                  isLoading={isLoading}
                  loadingText={copy.verifying}
                >
                  {copy.verifyAndSignIn}
                </ButtonLoader>
              </Button>

              <div className="mt-4 flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={handleResendCode}
                  className="text-primary text-sm font-medium hover:underline"
                  disabled={isLoading}
                >
                  {copy.resendCode}
                </button>
                <span className="text-muted-foreground text-sm">•</span>
                <button
                  type="button"
                  onClick={() => {
                    const params = new URLSearchParams(searchParams.toString());
                    params.delete('step');
                    params.delete('email');
                    router.push(`${authBasePath}?${params.toString()}`);
                  }}
                  className="text-primary text-sm font-medium hover:underline"
                  disabled={isLoading}
                >
                  {copy.backToSignIn}
                </button>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  }

  if (step === 'reactivate') {
    return (
      <div className="w-full min-w-0">
        <div>
          <AuthFormHeading
            title={<>{copy.reactivateAccount}</>}
            description={
              <>
                {reactivationPhase === 'otp'
                  ? copy.reactivationOtpDescription
                  : copy.reactivationPromptDescription}
              </>
            }
          />
          {pendingEmail ? (
            <p className="text-muted-foreground mt-2 text-sm">
              {copy.emailWithValue}{' '}
              <span className="font-medium">{pendingEmail}</span>
            </p>
          ) : null}
        </div>
        <div>
          {reactivationPhase === 'otp' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleReactivationSubmit();
              }}
              className={AUTH_FORM_SPACING}
            >
              <div className="flex flex-col items-center">
                <Label
                  htmlFor={otpInputId}
                  className="text-center text-sm font-medium"
                >
                  {copy.reactivationCode}
                </Label>
                <div ref={otpContainerRef} className="w-full">
                  <InputOTP
                    id={otpInputId}
                    maxLength={6}
                    autoFocus
                    containerClassName="justify-center"
                    value={otpCode}
                    onChange={setOtpCode}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <AuthFormError>{error}</AuthFormError>

              <Button
                type="submit"
                className={AUTH_PRIMARY_BUTTON}
                disabled={isLoading || !isOtpCodeComplete}
              >
                <ButtonLoader
                  isLoading={isLoading}
                  loadingText={copy.reactivating}
                >
                  {copy.reactivateAndSignIn}
                </ButtonLoader>
              </Button>

              <div className="mt-4 flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={handleStartReactivation}
                  className="text-primary text-sm font-medium hover:underline"
                  disabled={isLoading}
                >
                  {copy.resendCode}
                </button>
                <span className="text-muted-foreground text-sm">•</span>
                <button
                  type="button"
                  onClick={goToSignInStep}
                  className="text-primary text-sm font-medium hover:underline"
                  disabled={isLoading}
                >
                  {copy.backToSignIn}
                </button>
              </div>
            </form>
          ) : (
            <div className={AUTH_FORM_SPACING}>
              <div className="rounded-md border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-950">
                {copy.reactivationDataNotice}
              </div>

              <AuthFormError>{error}</AuthFormError>

              <Button
                type="button"
                className={AUTH_PRIMARY_BUTTON}
                disabled={isLoading}
                onClick={handleStartReactivation}
              >
                <ButtonLoader
                  isLoading={isLoading}
                  loadingText={copy.sendingCode}
                >
                  {copy.sendReactivationCode}
                </ButtonLoader>
              </Button>

              <Button
                type="button"
                variant="ghost"
                className={AUTH_PRIMARY_BUTTON}
                disabled={isLoading}
                onClick={goToSignInStep}
              >
                {copy.backToSignIn}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}
